// @flow

// ImageOnlyAttributionText is one of the many components that can get rendered
// in the Footer of a PinRep. Unlike Pin titles, which are specific to the Pin,
// or normal Attribution<Name/Title/Avatar> which show users,
// ImageOnlyAttributionText is built to explain to the user why she is seeing
// this Pin. It usually displays text such as "Picked for you" or "From Women's
// Fashion".

import React from 'react';
import CloseupLink from 'app/common/react/components/PinRep/components/CloseupLink';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import Text from 'app/common/react/components/common/ExperimentalLegoGestalt/Text';
import { Box } from 'gestalt';
import { normalizeSearchQuery } from 'app/common/react/components/discover/lib/searchQueryUtils';
import { type FeedbackType } from 'app/common/react/components/PinRep/feedback-types';
import { type Pin } from 'app/common/react/components/PinRep/types';

type Props = {|
  feedbackType: FeedbackType,
  pin: Pin,
|};

export default function ImageOnlyAttributionText({ feedbackType, pin }: Props) {
  const { pinner, source_interest: sourceInterest } = pin;
  const { full_name: pinnerName, username } = pinner || {};
  const { name: topic } = sourceInterest || {};

  const isTopic = feedbackType === 'topic';
  const isFollowed = !isTopic && feedbackType === 'followed';
  const pinnerUrl = `/${username}/`;
  const searchQuery = normalizeSearchQuery(topic);
  const searchUrl = `/search/pins/?q=${encodeURIComponent(searchQuery)}&rs=image_only_attribution`;
  const navigateUrl = isTopic ? searchUrl : pinnerUrl;

  const feedbackTypes: { [key: string]: string } = {
    topic: i18n._('From {topic}', 'attribution on pin rep'),
    followed: i18n._('From {pinnerName}', 'attribution on pin rep'),
    pfyBoard: i18n._('Picked for you', 'attribution on pin rep'),
    pfy: i18n._('Picked for you', 'attribution on pin rep'),
  };

  const attribution = feedbackTypes[feedbackType];

  if (!attribution) {
    return null;
  }

  if (isFollowed || isTopic) {
    const isStart = attribution.indexOf('{') === 0;
    const topicOrPinnerName = isTopic ? topic : pinnerName;
    const attributionText = isStart
      ? attribution.slice(attribution.indexOf('}') + 1)
      : attribution.slice(0, attribution.indexOf('{'));

    return isStart ? (
      <Box>
        <NavigateLink to={navigateUrl}>
          <Text weight="bold" inline size="sm" truncate>
            {topicOrPinnerName}
          </Text>
        </NavigateLink>
        <Text inline size="sm">
          {attributionText}
        </Text>
      </Box>
    ) : (
      <Box>
        <Text inline size="sm">
          {attributionText}
        </Text>
        <NavigateLink to={navigateUrl}>
          <Text color="darkGray" weight="bold" inline size="sm" truncate>
            {topicOrPinnerName}
          </Text>
        </NavigateLink>
      </Box>
    );
  }

  return (
    <Box>
      <CloseupLink pin={pin}>
        <Text color="darkGray" overflow="normal" size="sm" weight="semibold">
          {attribution}
        </Text>
      </CloseupLink>
    </Box>
  );
}
